import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLista } from '../api/lista';
import QRCode from 'qrcode';
import { api, BladApi } from '../api/client';
import type { Egzemplarz, Indeks, Lista, Lokalizacja } from '../api/typy';
import { Karta, Komunikat, Ladowanie, Pole, Przycisk, Pusto, Wybor, Zakladki } from '../components/ui';
import { Skaner } from '../components/Skaner';

interface EtykietaSprzetu {
  rodzaj: 'SPRZET'; kod: string; nrCsm: string; nrKwestury: string | null;
  nazwa: string; model: string | null; serialNumber: string | null; lokalizacja: string | null;
}
interface EtykietaLokalizacji {
  rodzaj: 'LOKALIZACJA'; kod: string; nazwa: string; sciezka: string | null; rodzajLokalizacji: string;
}
type Etykieta = EtykietaSprzetu | EtykietaLokalizacji;

/**
 * Arkusz etykiet QR. Kody rysuje przeglądarka, a wydruk robi się przez „Drukuj”
 * z zapisem do PDF — dzięki temu etykiety wychodzą w rozdzielczości drukarki,
 * a polskie znaki nie zależą od czcionek zainstalowanych na serwerze.
 */
export function Etykiety() {
  const [zakladka, setZakladka] = useState<'sprzet' | 'lokalizacje' | 'skaner'>('sprzet');

  return (
    <div className="flex flex-col gap-5">
      <div className="etykiety-ukryj-w-druku">
        <h1 className="text-xl font-semibold text-kamien-900">Etykiety i skanowanie</h1>
        <p className="mt-1 text-sm text-kamien-500">
          Kod QR niesie numer ewidencyjny CSM albo kod lokalizacji — ten sam ciąg, którego szuka wyszukiwarka.
        </p>
      </div>

      <div className="etykiety-ukryj-w-druku">
        <Zakladki
          wartosc={zakladka} zmien={setZakladka}
          opcje={[
            { wartosc: 'sprzet', etykieta: 'Etykiety sprzętu' },
            { wartosc: 'lokalizacje', etykieta: 'Etykiety lokalizacji' },
            { wartosc: 'skaner', etykieta: 'Skanuj kod' },
          ]}
        />
      </div>

      {zakladka === 'skaner' ? <PanelSkanera /> : <ArkuszEtykiet rodzaj={zakladka} />}
    </div>
  );
}

function ArkuszEtykiet({ rodzaj }: { rodzaj: 'sprzet' | 'lokalizacje' }) {
  const [filtrId, setFiltrId] = useState('');
  const [zaznaczone, setZaznaczone] = useState<Set<string>>(new Set());

  const kartoteka = useLista({
    queryKey: ['kartoteka', 'typy-sprzetu'],
    queryFn: () => api.get<Lista<Indeks>>('/kartoteka?type=EQUIPMENT&naStronie=200'),
    enabled: rodzaj === 'sprzet',
  });

  const parametry = new URLSearchParams(
    rodzaj === 'sprzet' && filtrId ? { itemId: filtrId } : {},
  );

  const dane = useQuery({
    queryKey: ['etykiety', rodzaj, parametry.toString()],
    queryFn: () => api.get<{ pozycje: Etykieta[]; razem: number }>(`/etykiety/${rodzaj}?${parametry}`),
  });

  const naglowek = useQuery({
    queryKey: ['etykiety', 'naglowek'],
    queryFn: () => api.get<{ jednostka: string; nadrzedna: string }>('/etykiety/naglowek'),
  });

  const pozycje = dane.data?.pozycje ?? [];
  const doWydruku = zaznaczone.size > 0
    ? pozycje.filter((p) => zaznaczone.has(p.kod))
    : pozycje;

  if (dane.isLoading) return <Ladowanie />;

  return (
    <div className="flex flex-col gap-4">
      <div className="etykiety-ukryj-w-druku flex flex-col gap-4">
        {rodzaj === 'sprzet' && (
          <div className="max-w-md">
            <Wybor etykieta="Zawęź do typu sprzętu" value={filtrId} blad={kartoteka.blad}
              onChange={(e) => { setFiltrId(e.target.value); setZaznaczone(new Set()); }}>
              <option value="">— cały sprzęt —</option>
              {kartoteka.pozycje.map((i) => (
                <option key={i.id} value={i.id}>{i.code} — {i.name}</option>
              ))}
            </Wybor>
          </div>
        )}

        <Karta
          tytul={`Do wydruku: ${doWydruku.length} ${doWydruku.length === 1 ? 'etykieta' : 'etykiet'}`}
          opis="Zaznacz pozycje albo zostaw puste, żeby wydrukować wszystkie"
          akcje={
            <div className="flex gap-2">
              <Przycisk onClick={() => setZaznaczone(new Set())}>Wyczyść zaznaczenie</Przycisk>
              <Przycisk wariant="glowny" disabled={doWydruku.length === 0}
                onClick={() => window.print()}>
                Drukuj arkusz
              </Przycisk>
            </div>
          }
        >
          {pozycje.length === 0 ? (
            <Pusto tytul="Nie ma czego drukować"
              opis={rodzaj === 'sprzet'
                ? 'Załóż egzemplarze sprzętu, żeby wygenerować dla nich etykiety.'
                : 'Załóż lokalizacje, żeby wygenerować dla nich etykiety.'} />
          ) : (
            <ul className="max-h-72 divide-y divide-kamien-200 overflow-y-auto">
              {pozycje.map((p) => (
                <li key={p.kod}>
                  <label className="flex cursor-pointer items-center gap-3 px-5 py-2 text-sm hover:bg-kamien-50">
                    <input
                      type="checkbox" className="size-4 accent-bordo-600"
                      checked={zaznaczone.has(p.kod)}
                      onChange={(e) => setZaznaczone((s) => {
                        const n = new Set(s);
                        if (e.target.checked) n.add(p.kod); else n.delete(p.kod);
                        return n;
                      })}
                    />
                    <span className="font-mono text-xs text-kamien-500">{p.kod}</span>
                    <span className="flex-1 text-kamien-800">{p.nazwa}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </Karta>

        <Komunikat rodzaj="info">
          „Drukuj arkusz” otwiera okno drukowania przeglądarki — wybierz w nim „Zapisz jako PDF”,
          jeśli chcesz plik zamiast wydruku. Etykiety są rozmieszczone po trzy w rzędzie
          na stronie A4.
        </Komunikat>
      </div>

      <div className="arkusz-etykiet">
        <div className="etykiety-naglowek">
          {naglowek.data?.jednostka}
          {naglowek.data?.nadrzedna ? ` · ${naglowek.data.nadrzedna}` : ''}
        </div>
        <div className="etykiety-siatka">
          {doWydruku.map((p) => <Etykieta key={p.kod} dane={p} />)}
        </div>
      </div>
    </div>
  );
}

function Etykieta({ dane }: { dane: Etykieta }) {
  const plotno = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!plotno.current) return;
    // Wysoki margines korekcji: etykieta może się przybrudzić albo zagiąć,
    // a kod ma się nadal odczytać.
    void QRCode.toCanvas(plotno.current, dane.kod, {
      width: 132, margin: 1, errorCorrectionLevel: 'M',
      color: { dark: '#221a1d', light: '#ffffff' },
    });
  }, [dane.kod]);

  return (
    <div className="etykieta">
      <canvas ref={plotno} className="etykieta-qr" />
      <div className="etykieta-tresc">
        <p className="etykieta-kod">{dane.kod}</p>
        <p className="etykieta-nazwa">{dane.nazwa}</p>
        {dane.rodzaj === 'SPRZET' ? (
          <>
            {dane.model && <p className="etykieta-szczegol">{dane.model}</p>}
            {dane.serialNumber && <p className="etykieta-szczegol">s/n {dane.serialNumber}</p>}
            {dane.nrKwestury && <p className="etykieta-szczegol">śr. trwały {dane.nrKwestury}</p>}
          </>
        ) : (
          dane.sciezka && <p className="etykieta-szczegol">{dane.sciezka}</p>
        )}
      </div>
    </div>
  );
}

interface WynikSkanu {
  rodzaj: 'SPRZET' | 'INDEKS' | 'LOKALIZACJA';
  kod: string;
  egzemplarz?: Egzemplarz & { currentLocation: { code: string; name: string } | null };
  indeks?: Indeks;
  lokalizacja?: Lokalizacja;
}

function PanelSkanera() {
  const [wynik, setWynik] = useState<WynikSkanu | null>(null);
  const [blad, setBlad] = useState<string | null>(null);
  const [reczny, setReczny] = useState('');

  async function rozpoznaj(kod: string) {
    setBlad(null);
    try {
      setWynik(await api.get<WynikSkanu>(`/etykiety/rozpoznaj/${encodeURIComponent(kod)}`));
    } catch (e) {
      setWynik(null);
      setBlad(e instanceof BladApi ? e.message : 'Nie udało się rozpoznać kodu.');
    }
  }

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Karta tytul="Kamera" opis="Skan otwiera kartotekę zeskanowanej pozycji">
        <div className="px-5 py-5">
          <Skaner naKod={(kod) => void rozpoznaj(kod)} />
        </div>
      </Karta>

      <div className="flex flex-col gap-4">
        <Karta tytul="Albo wpisz kod ręcznie" opis="Działa też z czytnikiem USB">
          <form className="px-5 py-5"
            onSubmit={(e) => { e.preventDefault(); void rozpoznaj(reczny); }}>
            <Pole etykieta="Kod" value={reczny} onChange={(e) => setReczny(e.target.value)}
              placeholder="CSM/2026/00001, DG 328, kod kreskowy"
              podpowiedz="Enter rozpoznaje kod" />
          </form>
        </Karta>

        {blad && <Komunikat rodzaj="uwaga">{blad}</Komunikat>}

        {wynik && (
          <Karta tytul={
            wynik.rodzaj === 'SPRZET' ? 'Egzemplarz sprzętu'
              : wynik.rodzaj === 'INDEKS' ? 'Pozycja kartoteki' : 'Lokalizacja'
          }>
            <dl className="grid gap-x-6 gap-y-2 px-5 py-4 text-sm sm:grid-cols-2">
              {wynik.egzemplarz && (
                <>
                  <Para etykieta="Numer CSM" wartosc={wynik.egzemplarz.nrCsm} />
                  <Para etykieta="Typ" wartosc={wynik.egzemplarz.item.name} />
                  {wynik.egzemplarz.model && <Para etykieta="Model" wartosc={wynik.egzemplarz.model} />}
                  <Para etykieta="Stan techniczny" wartosc={wynik.egzemplarz.condition} />
                  {wynik.egzemplarz.currentLocation && (
                    <Para etykieta="Lokalizacja"
                      wartosc={`${wynik.egzemplarz.currentLocation.code} ${wynik.egzemplarz.currentLocation.name}`} />
                  )}
                </>
              )}
              {wynik.indeks && (
                <>
                  <Para etykieta="Kod" wartosc={wynik.indeks.code} />
                  <Para etykieta="Nazwa" wartosc={wynik.indeks.name} />
                  <Para etykieta="Jednostka" wartosc={wynik.indeks.unit} />
                  <Para etykieta="Rodzaj"
                    wartosc={wynik.indeks.type === 'MATERIAL' ? 'materiał zużywalny' : 'sprzęt'} />
                </>
              )}
              {wynik.lokalizacja && (
                <>
                  <Para etykieta="Kod" wartosc={wynik.lokalizacja.code} />
                  <Para etykieta="Nazwa" wartosc={wynik.lokalizacja.name} />
                  {wynik.lokalizacja.path && <Para etykieta="Ścieżka" wartosc={wynik.lokalizacja.path} />}
                </>
              )}
            </dl>
          </Karta>
        )}
      </div>
    </div>
  );
}

function Para({ etykieta, wartosc }: { etykieta: string; wartosc: string }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-kamien-500">{etykieta}</dt>
      <dd className="text-kamien-800">{wartosc}</dd>
    </div>
  );
}
